import { Button } from "antd";
import { CloseOutlined, ExclamationOutlined } from "@ant-design/icons";
import { PublisherLogo, PublisherKey } from "./PublisherLogo";
import "./ListingsListingsTab.scss";

type ListingStatus =
  | "verified"
  | "not_verified"
  | "wrong_phone"
  | "not_found"
  | "loading";

interface ListingRow {
  publisher: PublisherKey;
  publisherLabel: string;
  viewListingUrl?: string;
  businessName?: string;
  address?: string;
  phone?: string;
  status: ListingStatus;
}

const ROWS: ListingRow[] = [
  {
    publisher: "google",
    publisherLabel: "Google",
    viewListingUrl: "#",
    businessName: "Jam On Hawthorne",
    address: "2239 SE Hawthorne Blvd, Portland, OR 97214-3920",
    phone: "5032344790",
    status: "not_verified",
  },
  {
    publisher: "facebook",
    publisherLabel: "Facebook",
    viewListingUrl: "#",
    businessName: "Jam on Hawthorne",
    address: "2239 SE Hawthorne Blvd, Portland, OR 97214",
    status: "wrong_phone",
  },
  {
    publisher: "bing",
    publisherLabel: "Bing",
    status: "not_found",
  },
  {
    publisher: "yelp",
    publisherLabel: "Yelp",
    viewListingUrl: "#",
    businessName: "Jam On Hawthorne",
    address: "2239 SE Hawthorne Blvd, Portland, OR 97214",
    phone: "5032344790",
    status: "not_verified",
  },
  {
    publisher: "mapquest",
    publisherLabel: "MapQuest",
    viewListingUrl: "#",
    businessName: "Jam On Hawthorne",
    address: "2239 SE Hawthorne Blvd, Portland, OR 97214",
    phone: "5032344790",
    status: "not_verified",
  },
  {
    publisher: "superpages",
    publisherLabel: "Superpages",
    status: "loading",
  },
  {
    publisher: "citysearch",
    publisherLabel: "Citysearch",
    status: "not_found",
  },
];

export function ListingsListingsTab() {
  const errorCount = ROWS.filter(
    (r) => r.status === "wrong_phone" || r.status === "not_found"
  ).length;
  const issuesDetected = ROWS.some(
    (r) => r.status !== "verified" && r.status !== "loading"
  );

  return (
    <div className="listings-tab-listings">
      <div className="lll-status-bar">
        <div className="lll-status-summary">
          <span className="lll-status-label">Listing Status</span>
          {issuesDetected && (
            <span className="lll-issues-pill">
              <span className="lll-issues-dot">
                <ExclamationOutlined />
              </span>
              <span>Issues detected</span>
            </span>
          )}
        </div>
        <Button
          type="primary"
          danger
          className="lll-fix-all-btn"
          disabled={errorCount === 0}
        >
          Fix all errors
        </Button>
      </div>

      <div className="lll-table">
        <div className="lll-thead">
          <div className="lll-th lll-col-publisher" />
          <div className="lll-th lll-col-name">Business Name</div>
          <div className="lll-th lll-col-address">Address</div>
          <div className="lll-th lll-col-phone">Phone</div>
          <div className="lll-th lll-col-status">Status</div>
        </div>

        <div className="lll-tbody">
          {ROWS.map((row) => (
            <ListingRowView key={row.publisher} row={row} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ListingRowView({ row }: { row: ListingRow }) {
  if (row.status === "loading") {
    return (
      <div className="lll-row lll-row-loading">
        <div className="lll-td lll-col-publisher">
          <PublisherLogo publisher={row.publisher} />
          <div className="lll-publisher-text">
            <div className="lll-publisher-name">{row.publisherLabel}</div>
          </div>
        </div>
        <div className="lll-td lll-col-loading-cell" data-span="3">
          LOADING...
        </div>
        <div className="lll-td lll-col-status" />
      </div>
    );
  }

  const isMissing = row.status === "not_found";

  return (
    <div className="lll-row">
      <div className="lll-td lll-col-publisher">
        <PublisherLogo publisher={row.publisher} />
        <div className="lll-publisher-text">
          <div className="lll-publisher-name">{row.publisherLabel}</div>
          {row.viewListingUrl && (
            <a
              className="lll-view-listing-link"
              href={row.viewListingUrl}
              target="_blank"
              rel="noreferrer"
            >
              View Listing
            </a>
          )}
        </div>
      </div>

      {isMissing ? (
        <div className="lll-td lll-col-missing" data-span="3">
          MISSING LISTING
        </div>
      ) : (
        <>
          <div className="lll-td lll-col-name">{row.businessName || ""}</div>
          <div className="lll-td lll-col-address">{row.address || ""}</div>
          <div className="lll-td lll-col-phone">{row.phone || ""}</div>
        </>
      )}

      <div className="lll-td lll-col-status">
        <StatusPill status={row.status} />
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: ListingStatus }) {
  if (status === "verified") {
    return <span className="lll-status-chip verified">Verified</span>;
  }
  if (status === "not_verified") {
    return (
      <span className="lll-status-chip warning">
        <span className="lll-chip-icon">
          <ExclamationOutlined />
        </span>
        <span>Not Verified Through Yext</span>
      </span>
    );
  }
  if (status === "wrong_phone") {
    return (
      <span className="lll-status-chip danger">
        <span className="lll-chip-icon">
          <CloseOutlined />
        </span>
        <span>Wrong Phone Number</span>
      </span>
    );
  }
  if (status === "not_found") {
    return (
      <span className="lll-status-chip danger">
        <span className="lll-chip-icon">
          <CloseOutlined />
        </span>
        <span>Not Found</span>
      </span>
    );
  }
  return null;
}
